/** Shared authenticated photo upload through the Worker media pipeline. */
import { beginPhotoUpload, endPhotoUpload, updatePhotoUpload } from './photoUploadProgress';

type Fetcher = typeof fetch;

export type PhotoUploadPhase = 'preparing' | 'reserving' | 'uploading' | 'processing';

export type PhotoUploadProgressEvent = {
  phase: PhotoUploadPhase;
  label: string;
  /** Overall 0–100 estimate across prepare → reserve → upload → process. */
  percent: number;
  loaded?: number;
  total?: number;
};

export type PhotoUploadProgressHandler = (event: PhotoUploadProgressEvent) => void;

type UploadResponse = { ok: boolean; status: number; json: () => Promise<any> };

function uploadFailure(status: number) {
  if (status === 401) return 'Your session expired. Sign in again to upload your photo.';
  if (status === 413) return 'That photo is too large to upload. Choose a smaller copy.';
  if (status === 429)
    return 'Photo uploads are busy. Keep your photo selected and try again shortly.';
  if (status >= 500)
    return 'Photo uploads are temporarily unavailable. Keep your photo selected and try again.';
  return 'The photo upload did not finish. Keep your photo selected and try again.';
}

async function checkedJson(response: UploadResponse) {
  // Worker/provider outages can return HTML or an empty body rather than API JSON.
  const body = await response.json().catch(() => null);
  if (!response.ok)
    throw new Error(
      typeof body?.error?.message === 'string' && body.error.message.trim()
        ? body.error.message
        : uploadFailure(response.status),
    );
  if (!body?.data || typeof body.data !== 'object')
    throw new Error('The photo server returned an incomplete response. Please try again.');
  return body.data;
}

export type PhotoUploadAvailability = { available: boolean; reason: string };

export async function memberPhotoUploadAvailability(
  apiBase: string,
  fetcher: Fetcher = fetch,
): Promise<PhotoUploadAvailability> {
  const unavailable = { available: false, reason: 'Photo uploads are temporarily unavailable.' };
  try {
    const response = await fetcher(`${apiBase}/capabilities`, {
      cache: 'no-store',
      signal: AbortSignal.timeout(5_000),
    });
    if (!response.ok) return unavailable;
    const body = await response.json();
    const available = body?.data?.media_uploads === true && body?.data?.photo_review === true;
    const reason = body?.data?.media_reason;
    return {
      available,
      reason: available
        ? ''
        : typeof reason === 'string' && reason.trim()
          ? reason.trim()
          : unavailable.reason,
    };
  } catch {
    return unavailable;
  }
}

export async function memberPhotoUploadsAvailable(apiBase: string, fetcher: Fetcher = fetch) {
  return (await memberPhotoUploadAvailability(apiBase, fetcher)).available;
}

export type MediaTargetType =
  'profile' | 'gym' | 'owned_item' | 'post' | 'story' | 'trade' | 'review' | 'unattached';

const READY_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_INPUT_BYTES = 40 * 1024 * 1024;
/** Modern phones can capture 24/48 MP originals; only the resized copy reaches the Worker. */
const MAX_SOURCE_PIXELS = 80_000_000;
/** Absolute Worker upload ceiling — client compression aims much lower. */
const MAX_OUTPUT_BYTES = 8 * 1024 * 1024;
/** Maximum prepared input edge supported by the hosted processor. */
export const MEMBER_PHOTO_MAX_EDGE = 2560;
/** Target the same master byte budget the processor enforces for stored variants. */
export const MEMBER_PHOTO_TARGET_BYTES = 900 * 1024;
/** Reuse only Blob objects actually prepared here; callers cannot mark raw files as trusted. */
const preparedMemberPhotos = new WeakSet<Blob>();

/** Normalize phone camera aliases (image/jpg, empty type) to a Worker-ready MIME. */
export function memberPhotoMime(type: string | undefined | null) {
  const value = (type ?? '').toLowerCase().trim();
  if (value === 'image/jpg' || value === 'image/pjpeg') return 'image/jpeg';
  return value;
}

async function decodePhotoBitmap(file: Blob): Promise<{
  source: CanvasImageSource;
  width: number;
  height: number;
  close: () => void;
}> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        close: () => bitmap.close(),
      };
    } catch {
      // Safari can open camera-library formats in <img> that ImageBitmap cannot decode.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('Use a JPEG, PNG, or WebP image.'));
      element.src = url;
    });
    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      close: () => URL.revokeObjectURL(url),
    };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: 'image/webp' | 'image/jpeg',
  quality: number,
) {
  return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));
}

/** Prefer WebP when the browser can encode it; otherwise fall back to JPEG. */
export async function encodeCompressedPhoto(
  canvas: HTMLCanvasElement,
  {
    targetBytes = MEMBER_PHOTO_TARGET_BYTES,
    maxBytes = MAX_OUTPUT_BYTES,
    formats = ['image/webp', 'image/jpeg'],
  }: {
    targetBytes?: number;
    maxBytes?: number;
    formats?: readonly ('image/webp' | 'image/jpeg')[];
  } = {},
): Promise<Blob> {
  const qualities = [0.82, 0.74, 0.66, 0.58, 0.5];
  let best: Blob | null = null;
  for (const type of formats) {
    for (const quality of qualities) {
      const blob = await canvasToBlob(canvas, type, quality);
      // Unsupported canvas encoders may silently return PNG instead of the requested format.
      if (!blob || blob.size < 1) continue;
      if (memberPhotoMime(blob.type) !== type) break;
      if (!best || blob.size < best.size) best = blob;
      if (blob.size <= targetBytes) {
        preparedMemberPhotos.add(blob);
        return blob;
      }
    }
  }
  if (best && best.size <= maxBytes) {
    preparedMemberPhotos.add(best);
    return best;
  }
  throw new Error('That image could not be prepared. Try a smaller photo.');
}

/**
 * Always decode, resize, and recompress before upload. Camera rolls and photo
 * library picks both get a compact WebP (JPEG fallback) under the master budget.
 */
export async function normalizeMemberPhoto(file: Blob): Promise<Blob> {
  if (preparedMemberPhotos.has(file)) return file;
  if (file.size < 1) throw new Error('Choose an image under 8 MB.');
  if (file.size > MAX_INPUT_BYTES)
    throw new Error('That camera photo is too large. Try a smaller picture.');
  let bitmap: Awaited<ReturnType<typeof decodePhotoBitmap>>;
  try {
    bitmap = await decodePhotoBitmap(file);
  } catch {
    throw new Error('Use a JPEG, PNG, or WebP image.');
  }
  try {
    if (bitmap.width < 1 || bitmap.height < 1 || bitmap.width * bitmap.height > MAX_SOURCE_PIXELS)
      throw new Error('That photo is too large to prepare. Choose a lower-resolution copy.');
    let edge = MEMBER_PHOTO_MAX_EDGE;
    let lastError: Error | null = null;
    // Shrink further if a dense photo still misses the compressed budget.
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const longest = Math.max(bitmap.width, bitmap.height);
      const scale = longest > edge ? edge / longest : 1;
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('This browser could not prepare that image.');
      // Flatten any alpha onto white so JPEG fallback stays opaque.
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, width, height);
      context.drawImage(bitmap.source, 0, 0, width, height);
      try {
        const prepared = await encodeCompressedPhoto(canvas, {
          maxBytes: MEMBER_PHOTO_TARGET_BYTES,
        });
        preparedMemberPhotos.add(prepared);
        return prepared;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('That image could not be prepared.');
        edge = Math.max(640, Math.round(edge * 0.75));
      }
    }
    throw lastError ?? new Error('That image could not be prepared. Try a smaller photo.');
  } finally {
    bitmap.close();
  }
}

function reportProgress(
  progressId: string | undefined,
  onProgress: PhotoUploadProgressHandler | undefined,
  event: PhotoUploadProgressEvent,
) {
  onProgress?.(event);
  if (!progressId) return;
  updatePhotoUpload(progressId, event.label, event.percent);
}

/** PUT/POST a binary body with upload byte progress (fetch cannot report upload %). */
export async function uploadBinaryWithProgress(
  url: string,
  input: {
    method?: string;
    token: string;
    blob: Blob;
    contentType: string;
    onProgress?: (loaded: number, total: number) => void;
    fetcher?: Fetcher;
  },
): Promise<UploadResponse> {
  // Node/unit tests have no XHR; keep the injectable fetcher path for those runs.
  if (typeof XMLHttpRequest === 'undefined') {
    const response = await (input.fetcher ?? fetch)(url, {
      method: input.method ?? 'POST',
      headers: {
        Authorization: `Bearer ${input.token}`,
        'Content-Type': input.contentType,
      },
      body: input.blob,
    });
    return {
      ok: response.ok,
      status: response.status,
      json: async () => response.json(),
    };
  }
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(input.method ?? 'POST', url);
    request.setRequestHeader('Authorization', `Bearer ${input.token}`);
    request.setRequestHeader('Content-Type', input.contentType);
    request.timeout = 90_000;
    request.upload.onprogress = (event) => {
      if (!event.lengthComputable || !input.onProgress) return;
      input.onProgress(event.loaded, event.total);
    };
    request.onload = () => {
      resolve({
        ok: request.status >= 200 && request.status < 300,
        status: request.status,
        json: async () => {
          try {
            return JSON.parse(request.responseText || '{}');
          } catch {
            return {};
          }
        },
      });
    };
    request.onerror = () => reject(new Error('The photo upload was interrupted. Try again.'));
    request.onabort = () => reject(new Error('The photo upload was cancelled.'));
    request.ontimeout = () =>
      reject(new Error('The photo upload took too long. Keep your photo selected and try again.'));
    request.send(input.blob);
  });
}

/** Reserve, upload, and return a ready media id. Caller attaches references as needed. */
export async function uploadMemberPhoto(
  apiBase: string,
  token: string,
  input: {
    blob: Blob;
    targetType: MediaTargetType;
    targetId: string;
    label?: string;
    onProgress?: PhotoUploadProgressHandler;
  },
  fetcher: Fetcher = fetch,
) {
  if (!token) throw new Error('Sign in to upload a photo.');
  const progressId = `media-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const label = input.label ?? 'Uploading photo';
  beginPhotoUpload(progressId, `${label}…`, 2);
  try {
    reportProgress(progressId, input.onProgress, {
      phase: 'preparing',
      label: 'Preparing photo…',
      percent: 8,
    });
    const blob = await normalizeMemberPhoto(input.blob);
    if (!READY_TYPES.has(memberPhotoMime(blob.type)))
      throw new Error('Use a JPEG, PNG, or WebP image.');
    if (blob.size < 1 || blob.size > MAX_OUTPUT_BYTES)
      throw new Error('Choose an image under 8 MB.');
    reportProgress(progressId, input.onProgress, {
      phase: 'reserving',
      label: 'Reserving upload…',
      percent: 18,
    });
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    const reservation = await checkedJson(
      await fetcher(`${apiBase}/media/reservations`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          kind: 'photo',
          contentType: memberPhotoMime(blob.type) || blob.type,
          byteSize: blob.size,
          targetType: input.targetType,
          targetId: input.targetId,
        }),
      }),
    );
    if (typeof reservation.reservationId !== 'string' || !reservation.reservationId)
      throw new Error('The photo server could not start your upload. Please try again.');
    reportProgress(progressId, input.onProgress, {
      phase: 'uploading',
      label: 'Uploading photo…',
      percent: 22,
      loaded: 0,
      total: blob.size,
    });
    const uploadResponse = await uploadBinaryWithProgress(
      `${apiBase}/media/uploads/${encodeURIComponent(reservation.reservationId)}`,
      {
        token,
        blob,
        contentType: memberPhotoMime(blob.type) || blob.type,
        fetcher,
        onProgress: (loaded, total) => {
          const ratio = total > 0 ? loaded / total : 0;
          reportProgress(progressId, input.onProgress, {
            phase: 'uploading',
            label: 'Uploading photo…',
            percent: 22 + Math.round(ratio * 68),
            loaded,
            total,
          });
        },
      },
    );
    const uploaded = await checkedJson(uploadResponse);
    reportProgress(progressId, input.onProgress, {
      phase: 'processing',
      label: 'Finishing photo…',
      percent: 94,
    });
    if (uploaded.status !== 'ready')
      throw new Error('Your photo is still being processed. Try again in a moment.');
    if (typeof uploaded.mediaId !== 'string' || !uploaded.mediaId)
      throw new Error('The photo server returned an incomplete response. Please try again.');
    reportProgress(progressId, input.onProgress, {
      phase: 'processing',
      label: 'Photo ready',
      percent: 100,
    });
    return {
      mediaId: uploaded.mediaId as string,
      reservationId: reservation.reservationId as string,
    };
  } finally {
    endPhotoUpload(progressId);
  }
}

export function memberMediaImageUrl(
  apiBase: string,
  mediaId: string,
  variant: 'master' | 'feed' | 'thumbnail' = 'feed',
) {
  return `${apiBase}/media/${encodeURIComponent(mediaId)}/image?variant=${variant}`;
}
